import { noop } from 'utils/noop';
import { ComponentType, ReactNode, useMemo } from 'react';
import { BasicSelect } from 'ui/selects/BasicSelect';
import {
  OnSelectHandler,
  SelectValueDisplayProps,
  SelectValueType,
  SelectionDetails,
} from 'ui/selects/types';
import { StyledSelectOptionsContainer } from './TimePicker.styles';
import { GetTimePickerTriggerArgs, getTimePickerTrigger } from './TimePickerTrigger';
import { TimeClockFormat, TimeIn12HourClock, TimeIn24HourClock, TimeSeries } from './types';
import { generateTimeSeries, getDefaultTimeValue, getFormattedTime } from './utils';

const timeSeriesToOption = (timeSeries: TimeSeries, format: TimeClockFormat) => {
  const { amOrPm, timeIn12HourClockWithoutAmPm } = timeSeries;
  const value = getFormattedTime(timeSeries, format);

  return {
    value,
    id: value,
    Suffix: amOrPm,
    label: timeIn12HourClockWithoutAmPm,
  };
};

export interface TimePickerProps extends GetTimePickerTriggerArgs {
  /** Currently selected time in 12-hour or 24-hour clock format */
  time?: TimeIn12HourClock | TimeIn24HourClock;
  /**  input time can be formatted in 24-hour or 12-hour clock (ex: 13:30 vs 1:30 PM)
   * **required for 24-hour clock inputs**
   */
  inputClockFormat?: TimeClockFormat;
  /** Component id */
  id?: string;
  /** Function to run when a new time is selected. */
  onSelect?: (
    time: [TimeIn24HourClock | TimeIn12HourClock],
    details?: SelectionDetails<TimeIn24HourClock | TimeIn12HourClock>,
  ) => void;
  /** Time increment in minutes */
  increment?: 15 | 30 | 60;
  /** Default display placeholder value text */
  placeholder?: string;
  /** Whether selection is invalid */
  isError?: boolean;
  /** Whether the component is disabled */
  isDisabled?: boolean;
  /** Optional decorator post label */
  Suffix?: ReactNode;
}

export const TimePicker = ({
  id,
  time,
  increment = 15,
  onSelect = noop,
  inputClockFormat = '12-hour',
  placeholder = 'Select a time',
  isError = false,
  hasClockIcon = false,
  hasChevronDownIcon = false,
  isDisabled = false,
  ...props
}: TimePickerProps) => {
  const options = useMemo(
    () => generateTimeSeries(increment).map((t) => timeSeriesToOption(t, inputClockFormat)),
    [increment, inputClockFormat],
  );

  const defaultValues = getDefaultTimeValue(time);
  const TimePickerTriggerComponent = useMemo(
    () =>
      getTimePickerTrigger({ hasClockIcon, hasChevronDownIcon }) as ComponentType<
        React.PropsWithChildren<SelectValueDisplayProps<SelectValueType>>
      >,
    [hasClockIcon, hasChevronDownIcon],
  );

  return (
    <BasicSelect
      isDisabled={isDisabled}
      defaultValues={defaultValues}
      placeholder={placeholder}
      options={options}
      id={id}
      data-test="time-picker"
      isError={isError}
      Trigger={TimePickerTriggerComponent}
      SelectOptionsContainer={StyledSelectOptionsContainer}
      onSelect={onSelect as OnSelectHandler<SelectValueType>}
      {...props}
    />
  );
};
